import { RemoveCircleIcon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { formatShortSlashDate } from "@vega-core/utils/formatDate";

import { CloseDealModal } from "../CloseDealModal/CloseDealModal";

type CloseDealCardProps = {
  dealId: string;
  isClosed: boolean;
  closingDate?: string;
  closedAt?: string;
};

export const CloseDealCard = ({
  dealId,
  isClosed,
  closingDate,
  closedAt,
}: CloseDealCardProps) => {
  const {
    value: isModalOpen,
    setTrue: openModal,
    setFalse: closeModal,
  } = useBoolean(false);

  const getSubtitle = () => {
    if (isClosed) {
      return closedAt
        ? m.coInvestments_closeDeal_card_closedSubtitle({
            date: formatShortSlashDate(closedAt),
          })
        : null;
    }

    return closingDate
      ? m.coInvestments_closeDeal_card_subtitle({
          date: formatShortSlashDate(closingDate),
        })
      : null;
  };

  const subtitle = getSubtitle();

  return (
    <>
      <div
        className="mt-10 rounded-lg border border-border/primary bg-background/primary p-4 flex flex-col gap-3 sm:flex-row items-center justify-between"
        data-testid="close-deal-card"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center rounded-full bg-background/destructive p-1.5">
            <RemoveCircleIcon />
          </div>
          <div>
            <h3 className="body-semi-large-semibold text-text/primary">
              {isClosed
                ? m.coInvestments_closeDeal_card_closedTitle()
                : m.coInvestments_closeDeal_card_title()}
            </h3>
            {subtitle ? (
              <p className="body-base-regular text-text/secondary">
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>
        {isClosed ? null : (
          <ThemedButton
            dataTestId="close-deal-button"
            onClick={openModal}
            size="default"
            type="button"
            variant="secondary"
          >
            {m.coInvestments_closeDeal_card_button()}
          </ThemedButton>
        )}
      </div>

      {isModalOpen ? (
        <CloseDealModal dealId={dealId} isOpen onClose={closeModal} />
      ) : null}
    </>
  );
};
